import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Project {
  projectKey: string;
  projectName: string;
}

interface GlobalContextType {
  email: string;
  userKey: string;
  userName: string;
  projects: Project[];
  selectedProjectKey: string;
  isLoading: boolean;
  setEmail: (email: string) => void;
  setUserKey: (userKey: string) => void;
  setUserName: (userName: string) => void;
  setProjects: (projects: Project[]) => void;
  setSelectedProjectKey: (projectKey: string) => void;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

const STORAGE_KEY = 'overwatchai_context';
const EXPIRATION_MS = 8 * 60 * 60 * 1000; // 8 hours expiry

export function GlobalProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState('');
  const [userKey, setUserKey] = useState('');
  const [userName, setUserName] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectKey, setSelectedProjectKey] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      const now = Date.now();
      
      // Check if expired
      if (data.expiresAt && now < data.expiresAt) {
        setEmail(data.email || '');
        setUserKey(data.userKey || '');
        setUserName(data.userName || '');
        setProjects(data.projects || []);
        setSelectedProjectKey(data.selectedProjectKey || data.projects?.[0]?.projectKey || '');
      } else {
        // Expired, clear storage
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  // Save to localStorage whenever values change
  useEffect(() => {
    if (!isLoading && userKey) {
      const expiresAt = Date.now() + EXPIRATION_MS;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        email, userKey, userName, projects, selectedProjectKey, expiresAt
      }));
    }
  }, [email, userKey, userName, projects, selectedProjectKey, isLoading]);

  return (
    <GlobalContext.Provider value={{
      email, userKey, userName, projects, selectedProjectKey, isLoading,
      setEmail, setUserKey, setUserName, setProjects, setSelectedProjectKey
    }}>
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobal() {
  const context = useContext(GlobalContext);
  if (!context) throw new Error('useGlobal must be used within GlobalProvider');
  return context;
}
